import React, { useState } from 'react';
import { Routes, Route, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useQuery } from 'react-query';
import axios from 'axios';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  CogIcon,
  PlusIcon,
  WrenchScrewdriverIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  ChartBarIcon,
  CalendarDaysIcon,
  BoltIcon,
  ShieldCheckIcon,
  DocumentMagnifyingGlassIcon,
  CameraIcon,
  QrCodeIcon,
} from '@heroicons/react/24/outline';
import EquipmentList from '../../components/equipment/EquipmentList';
import EquipmentForm from '../../components/equipment/EquipmentForm';
import MaintenanceScheduler from '../../components/equipment/MaintenanceScheduler';
import EquipmentReports from '../../components/equipment/EquipmentReports';

const EquipmentPage = () => {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState('overview');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const navigate = useNavigate();

  // Fetch equipment data
  const { data: equipmentData, isLoading } = useQuery('equipment', async () => {
    const response = await axios.get('/equipments');
    return response.data;
  });

  // Fetch maintenance data
  const { data: maintenanceData } = useQuery('maintenance', async () => {
    const response = await axios.get('/equipments/maintenance');
    return response.data;
  });

  // Données fictives enrichies
  const mockEquipment = [
    // Équipements Cardio
    { id: 'E001', name: 'Tapis de Course Technogym', serialNumber: 'TC-2023-001', manufacturer: 'Technogym', purchaseDate: '2022-01-15', lastMaintenanceDate: '2023-05-10', status: 'available', category: 'cardio', condition: 95, nextMaintenanceDate: '2023-11-10', location: 'Zone Cardio', photo: null },
    { id: 'E002', name: 'Vélo Elliptique Life Fitness', serialNumber: 'VE-2023-002', manufacturer: 'Life Fitness', purchaseDate: '2022-01-20', lastMaintenanceDate: '2023-05-15', status: 'available', category: 'cardio', condition: 90, nextMaintenanceDate: '2023-11-15', location: 'Zone Cardio', photo: null },
    { id: 'E003', name: 'Vélo Stationnaire Kettler', serialNumber: 'VS-2023-003', manufacturer: 'Kettler', purchaseDate: '2022-01-25', lastMaintenanceDate: '2023-05-20', status: 'maintenance', category: 'cardio', condition: 75, nextMaintenanceDate: '2023-11-20', location: 'Zone Cardio', photo: null },
    { id: 'E004', name: 'Rameur Concept2', serialNumber: 'RM-2023-004', manufacturer: 'Concept2', purchaseDate: '2022-02-01', lastMaintenanceDate: '2023-06-01', status: 'available', category: 'cardio', condition: 85, nextMaintenanceDate: '2023-12-01', location: 'Zone Cardio', photo: null },
    { id: 'E005', name: 'Stepper Matrix', serialNumber: 'ST-2023-005', manufacturer: 'Matrix', purchaseDate: '2022-02-05', lastMaintenanceDate: '2023-06-05', status: 'available', category: 'cardio', condition: 92, nextMaintenanceDate: '2023-12-05', location: 'Zone Cardio', photo: null },
    
    // Équipements Musculation
    { id: 'E006', name: 'Banc de Musculation Multifonction', serialNumber: 'BM-2023-006', manufacturer: 'Hammer', purchaseDate: '2022-02-10', lastMaintenanceDate: '2023-06-10', status: 'available', category: 'strength', condition: 88, nextMaintenanceDate: '2023-12-10', location: 'Zone Musculation', photo: null },
    { id: 'E007', name: 'Machine à Pectoraux Technogym', serialNumber: 'MP-2023-007', manufacturer: 'Technogym', purchaseDate: '2022-02-15', lastMaintenanceDate: '2023-06-15', status: 'available', category: 'strength', condition: 90, nextMaintenanceDate: '2023-12-15', location: 'Zone Musculation', photo: null },
    { id: 'E008', name: 'Leg Press Matrix', serialNumber: 'LP-2023-008', manufacturer: 'Matrix', purchaseDate: '2022-02-20', lastMaintenanceDate: '2023-06-20', status: 'available', category: 'strength', condition: 93, nextMaintenanceDate: '2023-12-20', location: 'Zone Musculation', photo: null },
    { id: 'E009', name: 'Machine à Triceps Life Fitness', serialNumber: 'MT-2023-009', manufacturer: 'Life Fitness', purchaseDate: '2022-03-01', lastMaintenanceDate: '2023-07-01', status: 'out-of-order', category: 'strength', condition: 40, nextMaintenanceDate: '2023-09-01', location: 'Zone Musculation', photo: null },
    { id: 'E010', name: 'Butterfly Machine Technogym', serialNumber: 'BF-2023-010', manufacturer: 'Technogym', purchaseDate: '2022-03-05', lastMaintenanceDate: '2023-07-05', status: 'available', category: 'strength', condition: 87, nextMaintenanceDate: '2024-01-05', location: 'Zone Musculation', photo: null },
    
    // Équipements Fonctionnels
    { id: 'E011', name: 'TRX Training System', serialNumber: 'TX-2023-011', manufacturer: 'TRX', purchaseDate: '2022-03-10', lastMaintenanceDate: '2023-07-10', status: 'available', category: 'functional', condition: 95, nextMaintenanceDate: '2024-01-10', location: 'Zone Fonctionnelle', photo: null },
    { id: 'E012', name: 'Kettlebells Set (4-20kg)', serialNumber: 'KB-2023-012', manufacturer: 'Rogue', purchaseDate: '2022-03-15', lastMaintenanceDate: '2023-07-15', status: 'available', category: 'functional', condition: 98, nextMaintenanceDate: '2024-01-15', location: 'Zone Fonctionnelle', photo: null },
    { id: 'E013', name: 'Bosu Ball Pro', serialNumber: 'BB-2023-013', manufacturer: 'Bosu', purchaseDate: '2022-03-20', lastMaintenanceDate: '2023-07-20', status: 'available', category: 'functional', condition: 80, nextMaintenanceDate: '2024-01-20', location: 'Zone Fonctionnelle', photo: null },
    { id: 'E014', name: 'Battle Ropes 15m', serialNumber: 'BR-2023-014', manufacturer: 'Xenios', purchaseDate: '2022-04-01', lastMaintenanceDate: '2023-08-01', status: 'available', category: 'functional', condition: 90, nextMaintenanceDate: '2024-02-01', location: 'Zone Fonctionnelle', photo: null },
    { id: 'E015', name: 'Medicine Ball Set (2-10kg)', serialNumber: 'MB-2023-015', manufacturer: 'Escape', purchaseDate: '2022-04-05', lastMaintenanceDate: '2023-08-05', status: 'maintenance', category: 'functional', condition: 75, nextMaintenanceDate: '2023-09-20', location: 'Zone Fonctionnelle', photo: null },
    
    // Accessoires
    { id: 'E016', name: 'Haltères Set (1-30kg)', serialNumber: 'HT-2023-016', manufacturer: 'Hammer', purchaseDate: '2022-04-10', lastMaintenanceDate: '2023-08-10', status: 'available', category: 'accessories', condition: 85, nextMaintenanceDate: '2024-02-10', location: 'Rack Accessoires', photo: null },
    { id: 'E017', name: 'Barres de Traction Murale', serialNumber: 'BT-2023-017', manufacturer: 'Gorilla', purchaseDate: '2022-04-15', lastMaintenanceDate: '2023-08-15', status: 'available', category: 'accessories', condition: 92, nextMaintenanceDate: '2024-02-15', location: 'Zone Musculation', photo: null },
    { id: 'E018', name: 'Tapis de Yoga (x20)', serialNumber: 'TY-2023-018', manufacturer: 'Manduka', purchaseDate: '2022-04-20', lastMaintenanceDate: '2023-08-20', status: 'available', category: 'accessories', condition: 88, nextMaintenanceDate: '2024-02-20', location: 'Salle de Cours', photo: null },
  ];

  // Données de maintenance enrichies
  const mockMaintenance = [
    { id: 'M001', equipmentId: 'E003', equipmentName: 'Vélo Stationnaire Kettler', type: 'repair', scheduledDate: '2023-08-25', status: 'scheduled', assignedTo: 'Ahmed Technicien', notes: 'Problème de résistance à réparer', estimatedCost: 15000 },
    { id: 'M002', equipmentId: 'E015', equipmentName: 'Medicine Ball Set (2-10kg)', type: 'inspection', scheduledDate: '2023-09-20', status: 'scheduled', assignedTo: 'Karim Maintenance', notes: 'Vérification des coutures', estimatedCost: 3000 },
    { id: 'M003', equipmentId: 'E009', equipmentName: 'Machine à Triceps Life Fitness', type: 'repair', scheduledDate: '2023-09-01', status: 'scheduled', assignedTo: 'Life Fitness Service', notes: 'Remplacement des câbles et poulie défectueuse', estimatedCost: 25000 },
    { id: 'M004', equipmentId: 'E001', equipmentName: 'Tapis de Course Technogym', type: 'maintenance', scheduledDate: '2023-11-10', status: 'scheduled', assignedTo: 'Service Technogym', notes: 'Entretien semestriel', estimatedCost: 8000 },
    { id: 'M005', equipmentId: 'E007', equipmentName: 'Machine à Pectoraux Technogym', type: 'maintenance', scheduledDate: '2023-12-15', status: 'scheduled', assignedTo: 'Service Technogym', notes: 'Entretien semestriel', estimatedCost: 7500 },
  ];

  // Utiliser les données du serveur ou les données fictives
  const equipment = equipmentData?.equipment || mockEquipment;
  const maintenance = maintenanceData?.maintenance || mockMaintenance;

  // Calculate statistics
  const stats = {
    totalEquipment: equipment.length,
    availableEquipment: equipment.filter(e => e.status === 'available').length,
    inMaintenanceEquipment: equipment.filter(e => e.status === 'maintenance').length,
    outOfOrderEquipment: equipment.filter(e => e.status === 'out-of-order').length,
    upcomingMaintenance: maintenance.filter(m => {
      const maintenanceDate = new Date(m.scheduledDate);
      const nextWeek = new Date();
      nextWeek.setDate(nextWeek.getDate() + 7);
      return maintenanceDate <= nextWeek && m.status === 'scheduled';
    }).length,
    overdueMaintenance: maintenance.filter(m => {
      const maintenanceDate = new Date(m.scheduledDate);
      return maintenanceDate < new Date() && m.status === 'scheduled';
    }).length,
  };

  const tabs = [
    { id: 'overview', name: 'Vue d\'ensemble', icon: ChartBarIcon },
    { id: 'equipment', name: 'Équipements', icon: CogIcon },
    { id: 'maintenance', name: 'Maintenance', icon: WrenchScrewdriverIcon },
    { id: 'reports', name: 'Rapports', icon: DocumentMagnifyingGlassIcon },
  ];

  const equipmentCategories = [
    { id: 'cardio', name: 'Cardio', icon: BoltIcon, color: 'bg-red-500' },
    { id: 'strength', name: 'Musculation', icon: CogIcon, color: 'bg-blue-500' },
    { id: 'functional', name: 'Fonctionnel', icon: ShieldCheckIcon, color: 'bg-green-500' },
    { id: 'accessories', name: 'Accessoires', icon: WrenchScrewdriverIcon, color: 'bg-purple-500' },
  ];

  // Données pour les graphiques
  const equipmentAgeData = [
    { age: '< 6 mois', count: 0 },
    { age: '6-12 mois', count: 0 },
    { age: '1-2 ans', count: 18 },
    { age: '2-3 ans', count: 0 },
    { age: '> 3 ans', count: 0 },
  ];

  const maintenanceTypeData = [
    { type: 'Réparation', count: 2 },
    { type: 'Maintenance', count: 2 },
    { type: 'Inspection', count: 1 },
  ];

  const equipmentConditionData = [
    { condition: "Excellent (90-100%)", count: 6 },
    { condition: "Bon (80-89%)", count: 9 },
    { condition: "Moyen (70-79%)", count: 2 },
    { condition: "Mauvais (<70%)", count: 1 },
  ];

  // Fonctions de gestion des actions principales
  const handleAddEquipment = () => {
    navigate('/equipment/new');
  };
  
  const handleProgramMaintenance = () => {
    navigate('/equipment/maintenance/new');
  };

  return (
    <Routes>
      <Route path="/" element={
        <div className="space-y-6">
          {/* Header */}
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-white">
                {t('equipment')}
              </h1>
              <p className="text-white/60 mt-2">
                Gestion complète des équipements et maintenance
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={handleProgramMaintenance}
                className="bg-gradient-to-r from-orange-600 to-red-600 text-white px-4 py-2 rounded-xl font-semibold hover:from-orange-700 hover:to-red-700 transition-all duration-300 flex items-center space-x-2"
              >
                <WrenchScrewdriverIcon className="h-5 w-5 mr-2" />
                <span>Programmer Maintenance</span>
              </button>
              <button
                onClick={handleAddEquipment}
                className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-2 rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-300 flex items-center space-x-2"
              >
                <PlusIcon className="h-5 w-5 mr-2" />
                <span>Nouvel Équipement</span>
              </button>
            </div>
          </div>

          {/* Equipment Status Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/60 text-sm font-medium">Total Équipements</p>
                  <p className="text-2xl font-bold text-white mt-2">{stats.totalEquipment}</p>
                </div>
                <div className="p-3 rounded-2xl bg-blue-500/20">
                  <CogIcon className="h-6 w-6 text-blue-400" />
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/60 text-sm font-medium">Disponibles</p>
                  <p className="text-2xl font-bold text-white mt-2">{stats.availableEquipment}</p>
                  <p className="text-white/50 text-sm mt-1">
                    {stats.totalEquipment > 0 ? Math.round((stats.availableEquipment / stats.totalEquipment) * 100) : 0}% opérationnels
                  </p>
                </div>
                <div className="p-3 rounded-2xl bg-green-500/20">
                  <CheckCircleIcon className="h-6 w-6 text-green-400" />
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/60 text-sm font-medium">En Maintenance</p>
                  <p className="text-2xl font-bold text-white mt-2">{stats.inMaintenanceEquipment}</p>
                  <p className="text-white/50 text-sm mt-1">
                    {stats.outOfOrderEquipment} hors service
                  </p>
                </div>
                <div className="p-3 rounded-2xl bg-yellow-500/20">
                  <WrenchScrewdriverIcon className="h-6 w-6 text-yellow-400" />
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/60 text-sm font-medium">Maintenance Prévue</p>
                  <p className="text-2xl font-bold text-white mt-2">{stats.upcomingMaintenance}</p>
                  <p className="text-red-400 text-sm mt-1">
                    {stats.overdueMaintenance} en retard
                  </p>
                </div>
                <div className="p-3 rounded-2xl bg-orange-500/20">
                  <CalendarDaysIcon className="h-6 w-6 text-orange-400" />
                </div>
              </div>
            </motion.div>
          </div>

          {/* Equipment Categories Overview */}
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
            <h3 className="text-xl font-semibold text-white mb-4">
              Répartition par Catégorie
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {equipmentCategories.map((category) => {
                const categoryEquipment = equipment.filter(e => e.category === category.id);
                const availableCount = categoryEquipment.filter(e => e.status === 'available').length;
                const totalCount = categoryEquipment.length;
                const percentage = totalCount > 0 ? (availableCount / totalCount * 100) : 0;
                
                return (
                  <div key={category.id} className="bg-white/5 rounded-xl p-4">
                    <div className="flex items-center space-x-3 mb-3">
                      <div className={`w-10 h-10 ${category.color} rounded-xl flex items-center justify-center`}>
                        <category.icon className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <h4 className="text-white font-medium">{category.name}</h4>
                        <p className="text-white/60 text-sm">{totalCount} équipements</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-white/70 text-sm">Disponibles: {availableCount}</span>
                      <span className={`text-sm font-semibold ${
                        percentage >= 80 ? 'text-green-400' : percentage >= 60 ? 'text-yellow-400' : 'text-red-400'
                      }`}>
                        {percentage.toFixed(0)}%
                      </span>
                    </div>
                    <div className="w-full bg-white/10 rounded-full h-2 mt-2">
                      <div 
                        className={`h-2 rounded-full ${
                          percentage >= 80 ? 'bg-green-500' : percentage >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                        }`}
                        style={{ width: `${percentage}%` }}
                      ></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Maintenance Schedule */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-white">
                Maintenance Planifiée
              </h3>
              <Link
                to="/equipment/maintenance"
                className="text-sm text-blue-400 hover:text-blue-300 transition-colors"
              >
                Voir tout
              </Link>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-white/5">
                  <tr>
                    <th className="px-6 py-3 text-left text-white font-semibold">Équipement</th>
                    <th className="px-6 py-3 text-left text-white font-semibold">Type</th>
                    <th className="px-6 py-3 text-left text-white font-semibold">Date</th>
                    <th className="px-6 py-3 text-left text-white font-semibold">Assigné à</th>
                    <th className="px-6 py-3 text-left text-white font-semibold">Statut</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {maintenance.slice(0, 4).map((m) => (
                    <tr key={m.id} className="hover:bg-white/5 transition-colors">
                      <td className="px-6 py-4 text-white">{m.equipmentName}</td>
                      <td className="px-6 py-4 text-white/80">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          m.type === 'repair' 
                            ? 'bg-red-500/20 text-red-400' 
                            : m.type === 'maintenance'
                            ? 'bg-blue-500/20 text-blue-400'
                            : 'bg-purple-500/20 text-purple-400'
                        }`}>
                          {m.type === 'repair' ? 'Réparation' : 
                          m.type === 'maintenance' ? 'Maintenance' : 'Inspection'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-white/80">{new Date(m.scheduledDate).toLocaleDateString('fr-FR')}</td>
                      <td className="px-6 py-4 text-white/80">{m.assignedTo}</td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          m.status === 'completed' 
                            ? 'bg-green-500/20 text-green-400' 
                            : m.status === 'in-progress'
                            ? 'bg-blue-500/20 text-blue-400'
                            : 'bg-yellow-500/20 text-yellow-400'
                        }`}>
                          {m.status === 'completed' ? 'Complété' : 
                          m.status === 'in-progress' ? 'En cours' : 'Planifié'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>

          {/* Equipment Statistics */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Equipment Age Distribution */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
            >
              <h3 className="text-xl font-semibold text-white mb-4">
                Âge des Équipements
              </h3>
              <div className="space-y-4">
                {equipmentAgeData.map((item) => (
                  <div key={item.age} className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-white/80 text-sm">{item.age}</span>
                      <span className="text-white font-medium">{item.count}</span>
                    </div>
                    <div className="w-full bg-white/10 rounded-full h-2">
                      <div 
                        className="h-2 rounded-full bg-blue-500"
                        style={{ width: `${(item.count / stats.totalEquipment * 100)}%` }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Maintenance Types */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
            >
              <h3 className="text-xl font-semibold text-white mb-4">
                Types de Maintenance
              </h3>
              <div className="space-y-4">
                {maintenanceTypeData.map((item) => (
                  <div key={item.type} className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-white/80 text-sm">{item.type}</span>
                      <span className="text-white font-medium">{item.count}</span>
                    </div>
                    <div className="w-full bg-white/10 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full ${
                          item.type === 'Réparation' ? 'bg-red-500' :
                          item.type === 'Maintenance' ? 'bg-blue-500' : 'bg-purple-500'
                        }`}
                        style={{ width: `${(item.count / maintenance.length * 100)}%` }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Equipment Condition */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
              className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
            >
              <h3 className="text-xl font-semibold text-white mb-4">
                État des Équipements
              </h3>
              <div className="space-y-4">
                {equipmentConditionData.map((item) => (
                  <div key={item.condition} className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-white/80 text-sm">{item.condition}</span>
                      <span className="text-white font-medium">{item.count}</span>
                    </div>
                    <div className="w-full bg-white/10 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full ${
                          item.condition.includes('Excellent') ? 'bg-green-500' :
                          item.condition.includes('Bon') ? 'bg-blue-500' :
                          item.condition.includes('Moyen') ? 'bg-yellow-500' : 'bg-red-500'
                        }`}
                        style={{ width: `${(item.count / stats.totalEquipment * 100)}%` }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Tab Navigation */}
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
            <div className="flex space-x-1">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${
                    activeTab === tab.id
                      ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                      : 'text-white/80 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <tab.icon className="h-5 w-5" />
                  <span className="hidden sm:inline">{tab.name}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div className="min-h-[500px]">
            {activeTab === 'overview' && (
              <div className="space-y-6">
                {/* Equipment Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-white/60 text-sm font-medium">Total Équipements</p>
                        <p className="text-2xl font-bold text-white mt-2">{stats.totalEquipment}</p>
                      </div>
                      <div className="p-3 rounded-2xl bg-blue-500/20">
                        <CogIcon className="h-6 w-6 text-blue-400" />
                      </div>
                    </div>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-white/60 text-sm font-medium">Disponibles</p>
                        <p className="text-2xl font-bold text-white mt-2">{stats.availableEquipment}</p>
                        <p className="text-white/50 text-sm mt-1">
                          {stats.totalEquipment > 0 ? Math.round((stats.availableEquipment / stats.totalEquipment) * 100) : 0}% opérationnels
                        </p>
                      </div>
                      <div className="p-3 rounded-2xl bg-green-500/20">
                        <CheckCircleIcon className="h-6 w-6 text-green-400" />
                      </div>
                    </div>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-white/60 text-sm font-medium">En Maintenance</p>
                        <p className="text-2xl font-bold text-white mt-2">{stats.inMaintenanceEquipment}</p>
                        <p className="text-white/50 text-sm mt-1">
                          {stats.outOfOrderEquipment} hors service
                        </p>
                      </div>
                      <div className="p-3 rounded-2xl bg-yellow-500/20">
                        <WrenchScrewdriverIcon className="h-6 w-6 text-yellow-400" />
                      </div>
                    </div>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-white/60 text-sm font-medium">Maintenance Prévue</p>
                        <p className="text-2xl font-bold text-white mt-2">{stats.upcomingMaintenance}</p>
                        <p className="text-red-400 text-sm mt-1">
                          {stats.overdueMaintenance} en retard
                        </p>
                      </div>
                      <div className="p-3 rounded-2xl bg-orange-500/20">
                        <CalendarDaysIcon className="h-6 w-6 text-orange-400" />
                      </div>
                    </div>
                  </motion.div>
                </div>

                {/* Equipment Categories Overview */}
                <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
                  <h3 className="text-xl font-semibold text-white mb-4">
                    Répartition par Catégorie
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    {equipmentCategories.map((category) => {
                      const categoryEquipment = equipment.filter(e => e.category === category.id);
                      const availableCount = categoryEquipment.filter(e => e.status === 'available').length;
                      const totalCount = categoryEquipment.length;
                      const percentage = totalCount > 0 ? (availableCount / totalCount * 100) : 0;
                      
                      return (
                        <div key={category.id} className="bg-white/5 rounded-xl p-4">
                          <div className="flex items-center space-x-3 mb-3">
                            <div className={`w-10 h-10 ${category.color} rounded-xl flex items-center justify-center`}>
                              <category.icon className="h-5 w-5 text-white" />
                            </div>
                            <div>
                              <h4 className="text-white font-medium">{category.name}</h4>
                              <p className="text-white/60 text-sm">{totalCount} équipements</p>
                            </div>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-white/70 text-sm">Disponibles: {availableCount}</span>
                            <span className={`text-sm font-semibold ${
                              percentage >= 80 ? 'text-green-400' : percentage >= 60 ? 'text-yellow-400' : 'text-red-400'
                            }`}>
                              {percentage.toFixed(0)}%
                            </span>
                          </div>
                          <div className="w-full bg-white/10 rounded-full h-2 mt-2">
                            <div 
                              className={`h-2 rounded-full ${
                                percentage >= 80 ? 'bg-green-500' : percentage >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                              }`}
                              style={{ width: `${percentage}%` }}
                            ></div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Maintenance Schedule */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
                >
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-semibold text-white">
                      Maintenance Planifiée
                    </h3>
                    <Link
                      to="/equipment/maintenance"
                      className="text-sm text-blue-400 hover:text-blue-300 transition-colors"
                    >
                      Voir tout
                    </Link>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-white/5">
                        <tr>
                          <th className="px-6 py-3 text-left text-white font-semibold">Équipement</th>
                          <th className="px-6 py-3 text-left text-white font-semibold">Type</th>
                          <th className="px-6 py-3 text-left text-white font-semibold">Date</th>
                          <th className="px-6 py-3 text-left text-white font-semibold">Assigné à</th>
                          <th className="px-6 py-3 text-left text-white font-semibold">Statut</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-white/10">
                        {maintenance.slice(0, 4).map((m) => (
                          <tr key={m.id} className="hover:bg-white/5 transition-colors">
                            <td className="px-6 py-4 text-white">{m.equipmentName}</td>
                            <td className="px-6 py-4 text-white/80">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                m.type === 'repair' 
                                  ? 'bg-red-500/20 text-red-400' 
                                  : m.type === 'maintenance'
                                  ? 'bg-blue-500/20 text-blue-400'
                                  : 'bg-purple-500/20 text-purple-400'
                              }`}>
                                {m.type === 'repair' ? 'Réparation' : 
                                m.type === 'maintenance' ? 'Maintenance' : 'Inspection'}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-white/80">{new Date(m.scheduledDate).toLocaleDateString('fr-FR')}</td>
                            <td className="px-6 py-4 text-white/80">{m.assignedTo}</td>
                            <td className="px-6 py-4">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                m.status === 'completed' 
                                  ? 'bg-green-500/20 text-green-400' 
                                  : m.status === 'in-progress'
                                  ? 'bg-blue-500/20 text-blue-400'
                                  : 'bg-yellow-500/20 text-yellow-400'
                              }`}>
                                {m.status === 'completed' ? 'Complété' : 
                                m.status === 'in-progress' ? 'En cours' : 'Planifié'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </motion.div>

                {/* Equipment Statistics */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* Equipment Age Distribution */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                    className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
                  >
                    <h3 className="text-xl font-semibold text-white mb-4">
                      Âge des Équipements
                    </h3>
                    <div className="space-y-4">
                      {equipmentAgeData.map((item) => (
                        <div key={item.age} className="space-y-2">
                          <div className="flex justify-between">
                            <span className="text-white/80 text-sm">{item.age}</span>
                            <span className="text-white font-medium">{item.count}</span>
                          </div>
                          <div className="w-full bg-white/10 rounded-full h-2">
                            <div 
                              className="h-2 rounded-full bg-blue-500"
                              style={{ width: `${(item.count / stats.totalEquipment * 100)}%` }}
                            ></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </motion.div>

                  {/* Maintenance Types */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 }}
                    className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
                  >
                    <h3 className="text-xl font-semibold text-white mb-4">
                      Types de Maintenance
                    </h3>
                    <div className="space-y-4">
                      {maintenanceTypeData.map((item) => (
                        <div key={item.type} className="space-y-2">
                          <div className="flex justify-between">
                            <span className="text-white/80 text-sm">{item.type}</span>
                            <span className="text-white font-medium">{item.count}</span>
                          </div>
                          <div className="w-full bg-white/10 rounded-full h-2">
                            <div 
                              className={`h-2 rounded-full ${
                                item.type === 'Réparation' ? 'bg-red-500' :
                                item.type === 'Maintenance' ? 'bg-blue-500' : 'bg-purple-500'
                              }`}
                              style={{ width: `${(item.count / maintenance.length * 100)}%` }}
                            ></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </motion.div>

                  {/* Equipment Condition */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.7 }}
                    className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
                  >
                    <h3 className="text-xl font-semibold text-white mb-4">
                      État des Équipements
                    </h3>
                    <div className="space-y-4">
                      {equipmentConditionData.map((item) => (
                        <div key={item.condition} className="space-y-2">
                          <div className="flex justify-between">
                            <span className="text-white/80 text-sm">{item.condition}</span>
                            <span className="text-white font-medium">{item.count}</span>
                          </div>
                          <div className="w-full bg-white/10 rounded-full h-2">
                            <div 
                              className={`h-2 rounded-full ${
                                item.condition.includes('Excellent') ? 'bg-green-500' :
                                item.condition.includes('Bon') ? 'bg-blue-500' :
                                item.condition.includes('Moyen') ? 'bg-yellow-500' : 'bg-red-500'
                              }`}
                              style={{ width: `${(item.count / stats.totalEquipment * 100)}%` }}
                            ></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                </div>
              </div>
            )}

            {activeTab === 'equipment' && <EquipmentList equipment={equipment} />}
            {activeTab === 'maintenance' && <MaintenanceScheduler maintenance={maintenance} />}
            {activeTab === 'reports' && <EquipmentReports equipment={equipment} maintenance={maintenance} />}
          </div>
        </div>
      } />
      <Route path="/new" element={<EquipmentForm />} />
      <Route path="/maintenance/new" element={<MaintenanceScheduler isNewMaintenance={true} />} />
      <Route path="/maintenance" element={<MaintenanceScheduler />} />
      <Route path="/reports" element={<EquipmentReports equipment={equipment} maintenance={maintenance} />} />
      <Route path="/:id/edit" element={<EquipmentForm />} />
    </Routes>
  );
};

export default EquipmentPage; 